import { ScrollingDoubleCanvasPlot } from './scrolling_double_canvas_plot'
import { interpolateArray } from './utils'
import { scaleLinear, scaleSequential, scaleLog, scaleQuantize } from 'd3-scale'
import { interpolateViridis } from 'd3-scale-chromatic'
import { axisLeft, axisBottom } from 'd3-axis'
import { zoom } from 'd3-zoom'
import { event } from 'd3-selection'
import { brush } from 'd3-brush'
import { transition } from 'd3-transition'
import { select } from 'd3-selection'
import * as viridis from 'scale-color-perceptual/hex/viridis.json'


export class SpectrogramDouble extends ScrollingDoubleCanvasPlot {

    private colorScale;
    private xScale;
    private yScale;
    private xAxis;
    private yAxis;
    private xAxisG;
    private yAxisG;
    private brush;
    private maxFreq;

    public constructor(key: string, container: HTMLElement, history: number, numFreqs: number, maxFreq: number, minY: number, maxY: number, config: Object={}) {
        super(key, container, history, numFreqs, config);

        this.maxFreq = maxFreq;
        // Create scale
        this.yScale = scaleLinear()
            .domain([0, maxFreq])
            .range([this.screenHeight, 0]);

        // Add scales to axis
        this.yAxis = axisLeft()
            .scale(this.yScale)
            .ticks(4);


        this.xScale = scaleLinear()
            .domain([history, 0])
            .range([0, this.screenWidth]);

        this.xAxis = axisBottom()
            .scale(this.xScale)
            .ticks(4);

        this.xAxisG = this.svg.append("g")
            .attr("class", "x-axis")
            .attr("transform", "translate(0," + this.screenHeight + ")")
            .call(this.xAxis);

        this.yAxisG = this.svg.append("g")
            .attr("class", "y-axis")
            // .attr("transform", "translate("+this.margin.left+", "+this.margin.top+")")
            .call(this.yAxis);

        // text label for the y axis
        this.svg.append("text")
            .attr("transform", "rotate(-90)")
            .attr("y", 0 - this.margin.left)
            .attr("x",0 - (this.screenHeight / 2))
            .attr("dy", "1em")
            .style("text-anchor", "middle")
            .text("Frequency (Hz)");

        if ("title" in config){
          this.svg.append("text")
            .attr("x", (this.screenWidth / 2))
            .attr("y", -2)
            .attr("text-anchor", "middle")
            .style("font-size", "16px")
            .text(config.title);
        }

        this.brush = brush( {
              input: function(x, e) { return x && [[x[0], e[0][1]], [x[1], e[1][1]]]; },
              output: function(xy) { return xy && [xy[0][0], xy[1][0]]; }
            })
          .extent( [ [0,0], [this.screenWidth,this.screenHeight] ] )
          .on("end", () => this.brushed())

        this.svg.append("g")
          .attr("class", "brush")
          .call(this.brush);

        this.svg.on("dblclick",() => {
              // console.log(this, this.xScale, this.xScale.domain());
              this.xScale.domain([history, 0]);
              this.yScale.domain([0, maxFreq]);
              this.xAxisG.transition().call(this.xAxis);
              this.yAxisG.transition().call(this.yAxis);
              this.svg.select(".brush").call(this.brush.move, null);
            });
        this.ctxA.imageSmoothingEnabled = false;
        this.ctxB.imageSmoothingEnabled = false;
        // this.ctx.scale(20, 20);
        const imageData = new ImageData(this.width, this.height);

        var buf = new ArrayBuffer(imageData.data.length);
        var buf8 = new Uint8ClampedArray(buf);
        var data = new Uint32Array(buf);

        // console.log(interpolateViridis(.5));
        let logScale = scaleLog().domain([minY, maxY]).clamp(true);
        let viridisScale = interpolateArray(viridis.default);
        this.colorScale = scaleSequential((d) => viridisScale(logScale(d)))


        if ('colorbar' in config) {
        var legend = this.svg.append("defs")
          .append("svg:linearGradient")
          .attr("id", "gradient")
          .attr("y2", "0%")
          .attr("x1", "100%")
          .attr("y1", "100%")
          .attr("x2", "100%")
          .attr("spreadMethod", "pad");
        const NUM_STOPS = 5;
        for (let i = 0; i < NUM_STOPS; i++) {
        legend.append("stop")
          .attr("offset", (100*i / (NUM_STOPS-1)) + "%")
          .attr("stop-color", interpolateViridis((i / (NUM_STOPS-1))))
          .attr("stop-opacity", 1);

        }


        this.svg.append("rect")
          .attr("width", 40)
          .attr("height", this.screenHeight)
          .style("fill", "url(#gradient)")
          .attr("transform", "translate("+(this.screenWidth+50)+",0)");

        var y = scaleLinear()
          .range([this.screenHeight, 0])
          .domain([minY, maxY]);

        var yAxis = axisLeft()
          .scale(y)
          .ticks(10);

        this.svg.append("g")
          .attr("class", "y axis")
          .attr("transform", "translate("+(this.screenWidth+50)+",0)")
          .call(yAxis)
          .append("text")
          .attr("transform", "rotate(-90)")
          .attr("y", 0)
          .attr("dy", ".71em")
          .style("text-anchor", "end")
          .text("axis title");
        }

        let pxColor = this.colorScale(0);
        for (var y = 0; y < this.height; ++y) {
            for (var x = 0; x < this.width; ++x) {
                data[y * this.width + x] =
                    (255 << 24) |    // alpha
                    (pxColor.b * 256 / 2 << 16) |    // blue
                    (pxColor.g * 256 << 8) |    // green
                    pxColor.r * 256;            // red
            }
        }

        imageData.data.set(buf8);
        this.ctxA.globalCompositeOperation = "copy";
        this.ctxB.globalCompositeOperation = "copy";
        this.ctxA.putImageData(imageData, 0, 0);
        this.ctxB.putImageData(imageData, 0, 0);

        // const imageData = this.ctx.getImageData(0, 0, this.canvasWidth, this.canvasHeight);
    }

    private brushed(){
      console.log(event)
      if (event.type == "end"){
        let s = event.selection;
        if (!s){

        } else {
          let newX = [s[0][0], s[1][0]].map(this.xScale.invert, this.xScale);
          let newY = [s[1][1], s[0][1]].map(this.yScale.invert, this.yScale);
          newX[1] = 0;
          console.log(newX, newY);
          this.xScale.domain(newX);
          this.yScale.domain(newY);
          this.svg.select(".brush").call(this.brush.move, null);
        }

        let yRange = this.yScale.domain()[1] - this.yScale.domain()[0];
        let xRange = this.xScale.domain()[0] - this.xScale.domain()[1];
        let yZoomFactor = this.maxFreq / yRange;
        let xZoomFactor = this.width / xRange;
        let height = yZoomFactor*this.screenHeight;
        let width = xZoomFactor*this.screenWidth;
        let top = (this.maxFreq - this.yScale.domain()[1]) * this.screenHeight / yRange;
        let left = 0;
        // console.log(yRange, yZoomFactor, height, top);
        console.log(xRange, xZoomFactor, width, left);
        this.canvasAD3.style("height", height+"px")
        this.canvasBD3.style("height", height+"px")
        this.canvasAD3.style("top", -top+"px")
        this.canvasBD3.style("top", -top+"px")
        this.canvasAD3.style("width", width+"px")
        this.canvasBD3.style("width", width+"px")

        // var t = this.svg.transition().duration(750);
        this.xAxisG.transition().call(this.xAxis);
        this.yAxisG.transition().call(this.yAxis);
        // this.yAxis.transition().call(this.yAxis);
        // svg.selectAll("circle").transition(t)
        //     .attr("cx", function(d) { return x(d[0]); })
        //     .attr("cy", function(d) { return y(d[1]); });
      }
    }

    protected drawNewColumns(colData: Array<Array<number>>){
      const numCols: number = colData.length;

      var buf = new ArrayBuffer(numCols * this.height * 4);
      var data8 = new Uint8ClampedArray(buf);
      var data32 = new Uint32Array(buf);

      // https://hacks.mozilla.org/2011/12/faster-canvas-pixel-manipulation-with-typed-arrays/
      // let data: Uint8ClampedArray = imageData.data;
      for (var x = 0; x < numCols; ++x) {
        for (var y = 0; y < this.height; ++y) {
          // let idx = (x+y*numCols)*4;
          let val = colData[x][y];
          let pxColor = this.colorScale(val);
          // data[idx++] = pxColor.r;
          // data[idx++] = pxColor.g;
          // data[idx++] = pxColor.b;
          // data[idx++] = 255;
          data32[(this.height - y - 1) * numCols + x] =
            (255   << 24) |    // alpha
            ( pxColor.b * 256 << 16) |    // blue
            ( pxColor.g * 256 <<  8) |    // green
              pxColor.r * 256;            // red
        }
      }

      const imageData = new ImageData(data8, numCols, this.height);
      this.ctxB.putImageData(imageData, this.shift, 0, 0, 0, numCols, this.height);
    }

}
